// Adapted from eval/dashboard/internal_web/src/lib/format.ts (ATM-57): the formatters this app uses, plus clockTime. Keep in step until ATM-60 shares it.
/**
 * Value formatting for the admin dashboard, from the eval dashboard's `lib/format.ts`.
 *
 * One rule dominates this module: a `null` is not a zero. A session that has not
 * recorded a value yet must say "we cannot know" rather than "0%", which a doctor reads
 * as "ruled out". Every formatter here therefore renders `null` as an em dash.
 */

/** What an unknowable value looks like. Never "0", never "n/a", never blank. */
export const EM_DASH = "—";

/** A 0–1 fraction as a percent, or an em dash when it is unknowable. */
export function pct(value: number | null, dp = 1): string {
  if (value === null || !Number.isFinite(value)) return EM_DASH;
  return `${(value * 100).toFixed(dp)}%`;
}

/**
 * The sign glyph for a signed figure, and the only place the rule is written.
 *
 * Three cases, not two: an exact tie is `±`, because "+0.0" reads as a small win and
 * "0.0" reads as a missing value. The negative glyph is U+2212 MINUS SIGN, not an ASCII
 * hyphen — it is the width of the plus, so a column of signed numbers stays aligned.
 */
export function signOf(value: number): "+" | "−" | "±" {
  return value > 0 ? "+" : value < 0 ? "−" : "±";
}

/** A snake_case vocabulary term as words: `time_course` → "time course". */
export function snakeWords(term: string): string {
  return term.replace(/_/g, " ").trim();
}

/** A rank as a doctor says it: 1 → "1st", 12 → "12th", 22 → "22nd". */
export function ordinal(rank: number): string {
  const teen = rank % 100 >= 11 && rank % 100 <= 13;
  const suffix = teen ? "th" : (["th", "st", "nd", "rd"][rank % 10] ?? "th");
  return `${rank}${suffix}`;
}

/** A count with thousands separators; `1_240` → `1,240`. */
export function count(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return EM_DASH;
  return value.toLocaleString("en-US");
}

/** A count with its noun, pluralised (`1, "question"` → `1 question`). Regular `-s` only. */
export function plural(value: number, noun: string): string {
  return `${count(value)} ${noun}${value === 1 ? "" : "s"}`;
}

/** Where `pctBounded` stops printing digits and starts saying "past here". */
const PCT_EPSILON = 0.001;

/**
 * A 0–1 fraction as a percent, with both ends bounded rather than rounded.
 *
 * For the differential's two columns. Likelihood is a saturating product of authored
 * knowledge-graph edge weights, which a real session records at 0.9999512216335408 for its
 * top entry, and confidence reaches its ceiling on a fully probed disease — so at both ends
 * the last tenth of a percent is where the interesting values sit. Rounding 0.9999512 to
 * "100.0%" asserts a completeness the value does not hold; rounding a live 0.0004 to "0.0%"
 * says a candidate was ruled out when it is merely far behind. An exact 1 or 0 prints
 * plainly, because there the bound is the value.
 */
export function pctBounded(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return EM_DASH;
  if (value < 1 && value > 1 - PCT_EPSILON / 2) return `> ${pct(1 - PCT_EPSILON)}`;
  if (value > 0 && value < PCT_EPSILON / 2) return `< ${pct(PCT_EPSILON)}`;
  return pct(value);
}

/** `semi_urgent` → `Semi-urgent`; an absent urgency dashes. */
export function urgencyLabel(value: string | null): string {
  if (!value) return EM_DASH;
  return value.replace(/_/g, "-").replace(/^./, (c) => c.toUpperCase());
}

/** Any string that may be blank, with the em dash as the blank rendering. */
export function orDash(value: string | null | undefined): string {
  return value === null || value === undefined || value.trim() === "" ? EM_DASH : value;
}

/** An ISO timestamp as a calendar date in the reader's locale, e.g. `Sep 25, 2026`. */
export function calendarDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** An ISO timestamp as a clock time in the reader's locale, e.g. `3:14 pm`. */
export function clockTime(iso: string): string {
  return new Date(iso)
    .toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
    .toLowerCase();
}
